import React, { useEffect, useState, useCallback } from 'react';
import { 
  TrendingUp, 
  Target, 
  CheckSquare, 
  Flame,
  Award,
  BookOpen,
  Calendar,
  BarChart3,
  PieChart,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import { scorecardApi } from '../lib/api';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart
} from 'recharts';
import { toast } from 'sonner';

const Scorecard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const loadScorecard = useCallback(async () => {
    try {
      const response = await scorecardApi.get();
      setData(response.data);
    } catch (error) {
      console.error('Failed to load scorecard:', error);
      toast.error('Failed to load scorecard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadScorecard();
  }, [loadScorecard]);

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-[#26C6DA]';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-emerald-500/20';
    if (score >= 60) return 'bg-[#26C6DA]/10';
    if (score >= 40) return 'bg-amber-100';
    return 'bg-red-100';
  };

  const COLORS = ['#0F5156', '#26C6DA', '#10B981', '#F59E0B', '#EF4444'];

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <ArrowUp className="w-4 h-4 text-emerald-500" />;
    if (current < previous) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-500 dark:text-slate-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-16 h-16 text-slate-600 dark:text-slate-300 mx-auto mb-4" />
        <p className="text-slate-500 dark:text-slate-400">No scorecard data available yet. Start using the app to see your progress!</p>
      </div>
    );
  }

  const pieData = [
    { name: 'Active', value: data.goals_by_status.active || 0 },
    { name: 'In Progress', value: data.goals_by_status.in_progress || 0 },
    { name: 'Completed', value: data.goals_by_status.completed || 0 },
    { name: 'Paused', value: data.goals_by_status.paused || 0 },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="scorecard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
          Strategic Scorecard
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Track your strategic progress and performance trends over time.
        </p>
      </div>

      {/* Overall Score Card */}
      <div className="bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-2xl p-8 text-white relative overflow-hidden" data-testid="overall-score-card">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1 text-center md:text-left">
            <p className="text-white/70 text-sm font-medium mb-2">Overall Score</p>
            <div className="text-6xl font-bold font-mono">{Math.round(data.overall_score)}%</div>
            <p className="text-white/70 text-sm mt-1">out of 100</p>
          </div>
          
          <div className="md:col-span-3 grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-2">
                <Flame className="w-8 h-8" />
              </div>
              <p className="text-2xl font-bold font-mono">{Math.round(data.consistency_score)}%</p>
              <p className="text-white/70 text-sm">Consistency</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-2">
                <Target className="w-8 h-8" />
              </div>
              <p className="text-2xl font-bold font-mono">{Math.round(data.progress_score)}%</p>
              <p className="text-white/70 text-sm">Progress</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-2">
                <BookOpen className="w-8 h-8" />
              </div>
              <p className="text-2xl font-bold font-mono">{Math.round(data.learning_score)}%</p>
              <p className="text-white/70 text-sm">Learning</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="metric-tasks">
          <div className="flex items-center justify-between mb-3">
            <CheckSquare className="w-5 h-5 text-[#26C6DA]" />
            <span className={`text-xs px-2 py-1 rounded-full ${getScoreBgColor(data.task_completion_rate)} ${getScoreColor(data.task_completion_rate)}`}>
              {data.task_completion_rate}%
            </span>
          </div>
          <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.total_tasks_completed}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Tasks Completed</p>
        </div>
        
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="metric-goals">
          <div className="flex items-center justify-between mb-3">
            <Target className="w-5 h-5 text-[#26C6DA]" />
            <span className={`text-xs px-2 py-1 rounded-full ${getScoreBgColor(data.goal_completion_rate)} ${getScoreColor(data.goal_completion_rate)}`}>
              {data.goal_completion_rate}%
            </span>
          </div>
          <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.total_goals_completed}/{data.total_goals_created}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Goals Achieved</p>
        </div>
        
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="metric-streak">
          <div className="flex items-center justify-between mb-3">
            <Flame className="w-5 h-5 text-amber-500" />
            <Award className="w-4 h-4 text-amber-500" />
          </div>
          <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.current_streak}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Day Streak (Best: {data.best_streak})</p>
        </div>
        
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="metric-learning">
          <div className="flex items-center justify-between mb-3">
            <BookOpen className="w-5 h-5 text-emerald-500" />
            <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
          </div>
          <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.total_prompts_answered}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">Prompts Answered</p>
        </div>
      </div>

      {/* Tabs for Charts */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white dark:bg-[#111827] p-1 rounded-full flex-wrap">
          <TabsTrigger 
            value="overview" 
            className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white"
            data-testid="tab-overview"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="habits" 
            className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white"
            data-testid="tab-habits"
          >
            Daily Habits
          </TabsTrigger>
          <TabsTrigger 
            value="weekly" 
            className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white"
            data-testid="tab-weekly"
          >
            Weekly Trends
          </TabsTrigger>
          <TabsTrigger 
            value="monthly" 
            className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white"
            data-testid="tab-monthly"
          >
            Monthly Trends
          </TabsTrigger>
          <TabsTrigger 
            value="goals" 
            className="rounded-full data-[state=active]:bg-[#26C6DA] data-[state=active]:text-white"
            data-testid="tab-goals"
          >
            Goals Analysis
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weekly Activity Chart */}
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="weekly-activity-chart">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
                Weekly Activity
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.weekly_trends}>
                    <defs>
                      <linearGradient id="colorTasks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0F5156" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0F5156" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorPrompts" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#26C6DA" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#26C6DA" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBE9E1" />
                    <XAxis dataKey="week_label" tick={{ fontSize: 12 }} stroke="#64748B" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #EBE9E1',
                        borderRadius: '12px'
                      }} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="tasks_completed" 
                      stroke="#0F5156" 
                      fillOpacity={1} 
                      fill="url(#colorTasks)" 
                      name="Tasks Completed"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="prompts_answered" 
                      stroke="#26C6DA" 
                      fillOpacity={1} 
                      fill="url(#colorPrompts)" 
                      name="Prompts Answered"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Goals Distribution */}
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="goals-distribution-chart">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
                Goals by Status
              </h3>
              <div className="h-64">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400">
                    <div className="text-center">
                      <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No goals created yet</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Daily Habits Tab */}
        <TabsContent value="habits" className="space-y-6">
          {data.daily_habits && data.daily_habits.total_daily_tasks > 0 ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="daily-habits-total">
                  <div className="flex items-center justify-between mb-3">
                    <Flame className="w-5 h-5 text-amber-500" />
                  </div>
                  <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.daily_habits.total_daily_tasks}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Daily Habits</p>
                </div>
                
                <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="daily-habits-avg">
                  <div className="flex items-center justify-between mb-3">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    <span className={`text-xs px-2 py-1 rounded-full ${getScoreBgColor(data.daily_habits.avg_completion_rate)} ${getScoreColor(data.daily_habits.avg_completion_rate)}`}>
                      {data.daily_habits.avg_completion_rate}%
                    </span>
                  </div>
                  <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.daily_habits.avg_completion_rate}%</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Avg Completion</p>
                </div>
                
                <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="daily-habits-streak">
                  <div className="flex items-center justify-between mb-3">
                    <Award className="w-5 h-5 text-amber-500" />
                  </div>
                  <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.daily_habits.best_streak}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Best Streak</p>
                </div>
                
                <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="daily-habits-today">
                  <div className="flex items-center justify-between mb-3">
                    <CheckSquare className="w-5 h-5 text-emerald-500" />
                  </div>
                  <p className="text-3xl font-bold text-[#26C6DA] font-mono">{data.daily_habits.tasks_completed_today}/{data.daily_habits.total_daily_tasks}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Done Today</p>
                </div>
              </div>
              
              {/* Habit Details */}
              <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="daily-habits-details">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
                  Daily Habit Breakdown
                </h3>
                <div className="space-y-4">
                  {data.daily_habits.tasks.map((habit, index) => (
                    <div key={habit.task_id} className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-slate-800 dark:text-slate-100">{habit.title}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {habit.completed_days}/{habit.total_days} days
                            </span>
                            {habit.current_streak > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 flex items-center gap-1">
                                <Flame className="w-3 h-3" />
                                {habit.current_streak} day streak
                              </span>
                            )}
                            {habit.end_date && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400">
                                Ends {habit.end_date}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-2xl font-bold font-mono ${getScoreColor(habit.completion_percentage)}`}>
                            {habit.completion_percentage}%
                          </span>
                        </div>
                      </div>
                      <Progress value={habit.completion_percentage} className="h-2 bg-[#EBE9E1]" />
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-12 text-center">
              <Flame className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
                No Daily Habits Yet
              </h3>
              <p className="text-slate-500 dark:text-slate-400">
                Create recurring daily tasks to track your habits and see your consistency metrics here.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Weekly Trends Tab */}
        <TabsContent value="weekly" className="space-y-6">
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="weekly-trends-chart">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              8-Week Performance Trend
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.weekly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE9E1" />
                  <XAxis dataKey="week_label" tick={{ fontSize: 12 }} stroke="#64748B" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #EBE9E1',
                      borderRadius: '12px'
                    }}
                    formatter={(value, name) => {
                      if (name === 'Avg Goal Progress %') return [`${value}%`, name];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="tasks_completed" 
                    stroke="#0F5156" 
                    strokeWidth={2}
                    dot={{ fill: '#0F5156' }}
                    name="Tasks Completed"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="prompts_answered" 
                    stroke="#26C6DA" 
                    strokeWidth={2}
                    dot={{ fill: '#26C6DA' }}
                    name="Prompts Answered"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="goals_progress" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ fill: '#10B981' }}
                    name="Avg Goal Progress %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Weekly Stats Table */}
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6 overflow-x-auto">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              Weekly Breakdown
            </h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-[#1F2937]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Week</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Tasks Created</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Tasks Done</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Prompts</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Goal Progress</th>
                </tr>
              </thead>
              <tbody>
                {data.weekly_trends.map((week, index) => (
                  <tr key={week.week} className="border-b border-slate-200 dark:border-[#1F2937]/50 hover:bg-slate-100 dark:bg-[#1A1A2E]/30">
                    <td className="py-3 px-4 font-medium text-slate-800 dark:text-slate-100">{week.week_label}</td>
                    <td className="py-3 px-4 text-center text-slate-500 dark:text-slate-400">{week.tasks_created}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-mono text-[#26C6DA]">{week.tasks_completed}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-mono text-[#26C6DA]">{week.prompts_answered}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-mono ${getScoreColor(week.goals_progress)}`}>
                        {week.goals_progress}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Monthly Trends Tab */}
        <TabsContent value="monthly" className="space-y-6">
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="monthly-trends-chart">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              6-Month Performance Overview
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EBE9E1" />
                  <XAxis dataKey="month_label" tick={{ fontSize: 12 }} stroke="#64748B" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748B" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #EBE9E1',
                      borderRadius: '12px'
                    }} 
                  />
                  <Legend />
                  <Bar dataKey="tasks_completed" fill="#0F5156" name="Tasks Completed" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="goals_completed" fill="#26C6DA" name="Goals Completed" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="prompts_answered" fill="#10B981" name="Prompts Answered" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        {/* Goals Analysis Tab */}
        <TabsContent value="goals" className="space-y-6">
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="goals-analysis">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
              Goal Achievement Breakdown
            </h3>
            
            {data.goals_achievement.length > 0 ? (
              <div className="space-y-4">
                {data.goals_achievement.map((goal, index) => (
                  <div key={goal.goal_id} className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-slate-800 dark:text-slate-100">{goal.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#26C6DA]/10 text-[#26C6DA] capitalize">
                            {goal.category}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                            goal.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            goal.status === 'active' ? 'bg-[#26C6DA]/10 text-[#26C6DA]' :
                            'bg-slate-700/30 text-slate-500 dark:text-slate-400'
                          }`}>
                            {goal.status}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-2xl font-bold font-mono ${getScoreColor(goal.progress)}`}>
                          {goal.progress}%
                        </span>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {goal.key_results_completed}/{goal.key_results_count} KRs done
                        </p>
                      </div>
                    </div>
                    <Progress value={goal.progress} className="h-2 bg-[#EBE9E1]" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No goals to analyze yet. Create some goals to see your achievement breakdown!</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Scorecard;
